import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { suppressionsApi } from '@/api/suppressions'
import type { SuppressionEntryDto, SuppressionImportResultDto } from '@/types/suppressions'

/** Result of a mutation. `handled` is true when a server 422 was already toasted by the global handler. */
export interface MutationResult {
  ok: boolean
  error?: string
  handled?: boolean
}

export interface ImportMutationResult extends MutationResult {
  result?: SuppressionImportResultDto
}

export const SUPPRESSION_PAGE_SIZE = 25

/**
 * Loads + maintains one page of a company's ACTIVE suppression list (the view shows live opt-outs; the
 * `active` filter is fixed to true for now), with an address search, plus the admin mutations (manual
 * add, remove/re-subscribe, bulk import). Each mutation reloads the current page on success. A generation
 * token guards against stale loads when the company or search changes.
 */
export function useSuppressions(clientCompanyId: string) {
  const [items, setItems] = useState<SuppressionEntryDto[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0) // zero-based
  const requestIdRef = useRef(0)

  const load = useCallback(async () => {
    if (!clientCompanyId) return
    const reqId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    const res = await suppressionsApi.list(clientCompanyId, {
      search: search.trim() || undefined,
      active: true,
      skip: page * SUPPRESSION_PAGE_SIZE,
      take: SUPPRESSION_PAGE_SIZE,
    })
    if (requestIdRef.current !== reqId) return
    if (res.error) setError(res.error)
    if (res.data) {
      setItems(res.data.Items)
      setTotal(res.data.Total)
    }
    setLoading(false)
  }, [clientCompanyId, search, page])

  useEffect(() => {
    void load()
    return () => { requestIdRef.current++ }
  }, [load])

  const addManual = useCallback(async (email: string, reason?: string): Promise<MutationResult> => {
    if (!clientCompanyId) return { ok: false, error: 'No company selected.' }
    const res = await suppressionsApi.add(clientCompanyId, { Email: email.trim(), Reason: reason?.trim() || undefined })
    if (res.error || !res.data) {
      if (res.status === 422) return { ok: false, handled: true }
      return { ok: false, error: res.error || 'Could not add address.' }
    }
    await load()
    return { ok: true }
  }, [clientCompanyId, load])

  const remove = useCallback(async (email: string): Promise<MutationResult> => {
    if (!clientCompanyId) return { ok: false, error: 'No company selected.' }
    const res = await suppressionsApi.remove(clientCompanyId, email)
    // DELETE returns 204 (no body, no error). Any other error surfaces.
    if (res.error) {
      if (res.status === 422) return { ok: false, handled: true }
      return { ok: false, error: res.error }
    }
    await load()
    return { ok: true }
  }, [clientCompanyId, load])

  const importList = useCallback(async (emails: string[]): Promise<ImportMutationResult> => {
    if (!clientCompanyId) return { ok: false, error: 'No company selected.' }
    const res = await suppressionsApi.import(clientCompanyId, { Emails: emails })
    if (res.error || !res.data) {
      if (res.status === 422) return { ok: false, handled: true }
      return { ok: false, error: res.error || 'Import failed.' }
    }
    await load()
    return { ok: true, result: res.data }
  }, [clientCompanyId, load])

  return {
    items, total, loading, error,
    search, setSearch, page, setPage, pageSize: SUPPRESSION_PAGE_SIZE,
    reload: load, addManual, remove, importList,
  }
}
